'use client';

import { IngredientWithDosing, Product, ProductItem } from '../shoppingCart/types';
import React, {
  createContext,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { OrderItem } from '../../model/order/OrderItem';
import { getLeastExpensiveProductSize } from './helpers';
import { useDeserializeIngredients } from '../../model/order/IngredientSerializer';
import { changeOrderItemSize } from './useOrderItemMutation';
import { useAddError } from '../error/ErrorContext';
import { useAllProductItems } from '../../state/product/hooks/useAllProductItems';
import { useIngredientsForItem } from '../../state/ingredient/hooks/useIngredientsForItem';
import { AnalyticsIngredientListName } from '../../utils/analytics/analytics';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

type Context = {
  item: OrderItem;
  setItem: (item: OrderItem) => void;
  itemRef: MutableRefObject<OrderItem | null>;
  itemIngredients: IngredientWithDosing[];
};

const contextDefaultValues = {
  item: new OrderItem(),
  setItem: () => undefined,
  itemRef: null as unknown as MutableRefObject<OrderItem | null>, //cheating, but it will never actually be null
  itemIngredients: [],
  price: 0,
};

export const ProductContext = createContext<Context>(contextDefaultValues);

export const ProductContextProvider: React.FC<{
  children: ReactNode;
  product: Product | ProductItem | OrderItem | null;
}> = ({ product, children }) => {
  const [item, setItem] = useState<OrderItem | null>(product instanceof OrderItem ? product : null);
  const { products, isLoading } = useAllProductItems();
  const itemRef = useRef<OrderItem | null>(item);
  const { ingredients: itemIngredients } = useIngredientsForItem(item);
  const { isLoading: isDeserializerLoading, deserializeIngredients } = useDeserializeIngredients();
  const addError = useAddError();

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const [sizeIdAndIngredients] = useState<{
    sizeId: string | null;
    ingredientHash: string | null;
  }>({
    sizeId: searchParams.get('sizeId') || null,
    ingredientHash: searchParams.get('ingredients') || null,
  });

  useEffect(() => {
    if (isLoading || isDeserializerLoading || item || !product) {
      return;
    }

    const leastExpensive = getLeastExpensiveProductSize(product);

    const productItem = products.find(
      (productItem) =>
        productItem.productID === product.productID &&
        productItem.sizeId === leastExpensive?.productSizeID
    );

    if (!productItem) {
      throw new Error('Product item not found');
    }

    // Apply campaign restricitions from passed product
    if (product.restrictedModification) {
      productItem.restrictedModification = true;
    }

    let newItem = OrderItem.fromProductItem(productItem);
    const sizeId = sizeIdAndIngredients.sizeId ? parseInt(sizeIdAndIngredients.sizeId, 10) : null;

    if (sizeId && newItem.productSizes.some((size) => size.productSizeID === sizeId)) {
      const { ingredients, error } = deserializeIngredients(
        sizeId,
        sizeIdAndIngredients.ingredientHash
      );

      if (sizeId !== newItem.sizeId) {
        const { item } = changeOrderItemSize(newItem, products, ingredients, sizeId);
        newItem = item;
      }
      newItem.setIngredients(
        ingredients.map((ingredient) => ({
          ...ingredient,
          addedFrom: AnalyticsIngredientListName.Recipe,
        }))
      );

      if (error) {
        addError(error);
      }
    }

    if (searchParams.get('sizeId') || searchParams.get('ingredients')) {
      const params = new URLSearchParams(searchParams);
      params.delete('sizeId');
      params.delete('ingredients');

      router.replace(`${pathname}${params.size > 0 ? `?${params.toString()}` : ''}`, {
        scroll: false,
      });
    }
    queueMicrotask(() => {
      setItem(newItem);
      itemRef.current = newItem;
    });
  }, [
    isLoading,
    product,
    products,
    item,
    isDeserializerLoading,
    deserializeIngredients,
    addError,
    sizeIdAndIngredients.sizeId,
    sizeIdAndIngredients.ingredientHash,
    sizeIdAndIngredients,
    searchParams,
    router,
    pathname,
  ]);

  const handleSetItem = useCallback((newItem: OrderItem) => {
    setItem(newItem);
    itemRef.current = newItem;
  }, []);

  if (isLoading || !item) {
    return null;
  }

  return (
    <ProductContext.Provider
      value={{
        item,
        setItem: handleSetItem,
        itemRef,
        itemIngredients,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
